import React, { useState, useEffect, useRef, useMemo } from "react";
import "./WeekView.css";
import ModalCita from "./ModalCita";
import { getCitas, deleteCita } from "../../../../../services/citas";

const WeekView = ({ selectedDate, refresh, setRefresh, horario }) => {
   const daysOfWeek = [
  { label: "Lun", key: "Lunes" },
  { label: "Mar", key: "Martes" },
  { label: "Mié", key: "Miércoles" },
  { label: "Jue", key: "Jueves" },
  { label: "Vie", key: "Viernes" },
  { label: "Sab", key: "Sábado" },
  { label: "Dom", key: "Domingo" },
];

  const hours = Array.from({ length: 24 }, (_, i) => [
    `${String(i).padStart(2, "0")}:00`,
    `${String(i).padStart(2, "0")}:30`,
  ]).flat();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [citas, setCitas] = useState([]);
  const [citaSeleccionada, setCitaSeleccionada] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const containerRef = useRef(null);

  const { weekStart, weekEnd } = useMemo(() => {
    const adjustedDay = (selectedDate.getDay() + 6) % 7;
    const start = new Date(selectedDate);
    start.setDate(selectedDate.getDate() - adjustedDay);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { weekStart: start, weekEnd: end };
  }, [selectedDate]);

  const today = new Date();
  let highlightToday = false;
  let todayIndex = -1;
  if (today >= weekStart && today <= weekEnd) {
    highlightToday = true;
    todayIndex = (today.getDay() + 6) % 7;
  }

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      const scrollPos = highlightToday
        ? (currentTime.getHours() * 2 + (currentTime.getMinutes() >= 30 ? 1 : 0)) * 30
        : 8 * 2 * 30;
      containerRef.current.scrollTop = scrollPos;
    }
  }, [currentTime, highlightToday]);

  useEffect(() => {
    const fetchCitas = async () => {
      try {
        const data = await getCitas();
       
        const citasWeek = data.filter((cita) => {
          const citaDate = new Date(`${cita.fecha}T12:00:00Z`);
          return citaDate >= weekStart && citaDate <= weekEnd;
        });
        setCitas(citasWeek);
      } catch (error) {
        console.error("Error al obtener citas:", error);
      }
    };
    fetchCitas();
  }, [selectedDate, refresh, weekStart, weekEnd]);

  const overlaps = (a, b) => {
    return a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes;
  };

  const processedCitas = useMemo(() => {
    const citasCopy = citas.map((cita, idx) => {
      const [startHour, startMin] = cita.start_time.split(":").map(Number);
      const [endHour, endMin] = cita.end_time.split(":").map(Number);
      const citaDate = new Date(`${cita.fecha}T${cita.start_time}`);
      const dayIndex = (citaDate.getDay() + 6) % 7;
      return {
        ...cita,
        startMinutes: startHour * 60 + startMin,
        endMinutes: endHour * 60 + endMin,
        dayIndex,
        _idx: idx,
      };
    });
    
    
    const groups = {};
    citasCopy.forEach((c) => {
      if (!groups[c.dayIndex]) groups[c.dayIndex] = [];
      groups[c.dayIndex].push(c);
    });

    const processedGroups = [];
    Object.keys(groups).forEach((dayKey) => {
      const group = groups[dayKey];
      group.sort((a, b) => a.startMinutes - b.startMinutes);
      const n = group.length;
      const visited = Array(n).fill(false);
      const clusters = [];

      for (let i = 0; i < n; i++) {
        if (!visited[i]) {
          const cluster = [];
          const stack = [i];
          visited[i] = true;
          while (stack.length > 0) {
            const current = stack.pop();
            cluster.push(group[current]);
            for (let j = 0; j < n; j++) {
              if (!visited[j] && group[current] && overlaps(group[current], group[j])) {
                visited[j] = true;
                stack.push(j);
              }
            }
          }
          clusters.push(cluster);
        }
      }

      clusters.forEach((cluster) => {
        cluster.sort((a, b) => a.startMinutes - b.startMinutes);
        const columns = [];
        cluster.forEach((evento) => {
          let assigned = false;
          for (let i = 0; i < columns.length; i++) {
            if (evento.startMinutes >= columns[i]) {
              evento.column = i;
              columns[i] = evento.endMinutes;
              assigned = true;
              break;
            }
          }
          if (!assigned) {
            evento.column = columns.length;
            columns.push(evento.endMinutes);
          }
        });
        const totalColumns = columns.length;
        cluster.forEach((evento) => {
          evento.widthPercentage = 100 / totalColumns;
          evento.leftOffset = evento.column * (100 / totalColumns);
        });
      });

      processedGroups.push(...clusters.flat());
    });
    
    return processedGroups;
  }, [citas]);

  const calculateTop = (time) => {
    const [hour, minute] = time.split(":").map(Number);
    const blocks = hour * 2 + (minute >= 30 ? 1 : 0);
    return blocks * 30;
  };

  const calculateHeight = (start_time, end_time) => {
    const [sHour, sMin] = start_time.split(":").map(Number);
    const [eHour, eMin] = end_time.split(":").map(Number);
    const startBlocks = sHour * 2 + (sMin >= 30 ? 1 : 0);
    const endBlocks = eHour * 2 + (eMin >= 30 ? 1 : 0);
    return (endBlocks - startBlocks) * 30;
  };

  const currentTimePosition = () => {
    const [ch, cm] = [currentTime.getHours(), currentTime.getMinutes()];
    return (ch * 2 + (cm >= 30 ? 1 : 0)) * 30 + ((cm % 30) / 30) * 30;
  };

  const handleCellClick = (hour, dayIndex) => {
    const cellDate = new Date(weekStart);
    cellDate.setDate(weekStart.getDate() + dayIndex);
    setCitaSeleccionada({
      fecha: cellDate.toISOString().split("T")[0],
      start_time: hour,
      end_time: `${String(Number(hour.split(":")[0]) + 1).padStart(2, "0")}:${hour.split(":")[1]}`,
      motivo: "",
      paciente: "",
    });
    setMostrarModal(true);
  };

  const abrirModalEditarCita = (cita) => {
    setCitaSeleccionada(cita);
    setMostrarModal(true);
  };

  // --- Helpers para gris fuera de horario (no toca citas) ---
const SLOT = 30; // minutos
const toMins = (hhmm) => { const [H,M]=(hhmm||"00:00").split(":").map(Number); return H*60+M; };
const isWorkingSlot = (ranges, slotHHMM) => {
  if (!ranges || ranges.length === 0) return false;
  const start = toMins(slotHHMM), end = start + SLOT;
  return ranges.some(r => start < toMins(r.end) && toMins(r.start) < end);
};

const getDateForWeekDay = (dayIndex) => {
  const date = new Date(weekStart);
  date.setDate(weekStart.getDate() + dayIndex);
  return date.getDate();
};

return (
  <div className="week-view" ref={containerRef} style={{ position: "relative" }}>
    <div className="week-header">
      <div className="time-label"></div>
      {daysOfWeek.map((day, index) => (
        <div
          key={index}
          className={`day-header ${highlightToday && index === todayIndex ? "today" : ""}`}
        >
         {day.label} {getDateForWeekDay(index)}
        </div>
      ))}
    </div>

    <div className="week-body" style={{ position: "relative" }}>
      {hours.map((hour, rowIndex) => {
        const [startH, startM] = hour.split(":").map(Number);
        const blockStartMin = startH * 60 + startM;
        const blockEndMin = blockStartMin + 30;

        return (
          <div key={rowIndex} className="time-row">
            <div className="time-label">{rowIndex % 2 === 0 ? hour : ""}</div>

            {daysOfWeek.map((dayName, dayIndex) => {
              const hasCita = processedCitas.some((cita) => {
                return (
                  cita.dayIndex === dayIndex &&
                  cita.startMinutes < blockEndMin &&
                  cita.endMinutes > blockStartMin
                );
              });

              // 🔹 rangos del horario laboral para esa columna (día)
              const ranges = (horario && horario[dayName.key]) ? horario[dayName.key] : [];
              const off = !isWorkingSlot(ranges, hour);

              return (
                <div
                  key={`${rowIndex}-${dayIndex}`}
                  className={`time-cell ${highlightToday && dayIndex === todayIndex ? "today" : ""} ${off ? "off-hours" : ""}`}
                  onClick={() => handleCellClick(hour, dayIndex)}
                  style={{
                    backgroundColor: hasCita ? "#e3f2fd" : undefined,
                  }}
                >
                  {highlightToday && dayIndex === todayIndex && rowIndex === 0 && (
                    <div
                      className="current-time-line"
                      style={{ top: `${currentTimePosition()}px` }}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {Array.from({ length: 7 }).map((_, dayIndex) => (
        <div
          key={dayIndex}
          className="day-appointments"
          style={{
            position: "absolute",
            top: 0,
            left: `${8.2 + ((100 - 8.2) / 7) * dayIndex}%`,
            width: `${(100 - 9) / 7}%`,
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {processedCitas
            .filter((cita) => cita.dayIndex === dayIndex)
            .map((cita) => {
              const eventTop = calculateTop(cita.start_time);
              const eventHeight = calculateHeight(cita.start_time, cita.end_time);
              return (
                <div
                  key={cita.id + "-" + cita._idx}
                  className={`cita-block ${cita.status === "cancelada" ? "cancelada" : ""} ${cita.origen === "publico" ? "cita-en-linea" : ""}`}
                  style={{
                    position: "absolute",
                    top: `${eventTop}px`,
                    height: `${eventHeight}px`,
                    left: `${cita.leftOffset}%`,
                    width: `${Math.min(cita.widthPercentage, 100 - cita.leftOffset)}%`,
                    background: "#90caf9",
                    border: "1px solid #007bff",
                    borderLeft: cita.origen === "publico" ? "4px solid #23b4bc" : undefined,
                    boxSizing: "border-box",
                    overflow: "hidden",
                    fontSize: "12px",
                    color: "black",
                    padding: "2px",
                    pointerEvents: "auto",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    abrirModalEditarCita(cita);
                  }}
                >
                {cita.origen === "publico" && "🌐 "}{cita.paciente_nombre} - {cita.motivo}
                </div>
              );
            })}
        </div>
      ))}
    </div>

    {mostrarModal && (
      <ModalCita
        cita={citaSeleccionada}
        onGuardar={(nuevaCita) => {
          setCitas((prev) => {
            const index = prev.findIndex((c) => c.id === nuevaCita.id);
            if (index !== -1) {
              const copy = [...prev];
              copy[index] = nuevaCita;
              return copy;
            }
            return [...prev, nuevaCita];
          });
          setMostrarModal(false);
          setRefresh((prev) => !prev);
        }}
        onEliminar={async (id) => {
          const eliminado = await deleteCita(id);
          if (eliminado) {
            setCitas((prev) => prev.filter((c) => c.id !== id));
            setMostrarModal(false);
            setRefresh((prev) => !prev);
          } else {
            alert("Error al eliminar la cita en el servidor.");
          }
        }}
        onCerrar={() => setMostrarModal(false)}
      />
    )}
  </div>
);
};

export default WeekView;
